import React, { useEffect, useState, useMemo, useRef } from "react";
import { collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";
import "./ProtectedPage.css";

const ProtectedPage = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [uniqueValues, setUniqueValues] = useState({});
  const [filters, setFilters] = useState({
    operatingSystem: [],
    city: [],
    region: [],
    country: [],
    timestamp: [],
  });
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState("");

  // format date consistently (keeps your timestamp behavior intact)
  const formatDate = (ts) => {
    if (!ts) return "N/A";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleString(); // local, readable
  };

  // Check auth (kept pattern, switched to onAuthStateChanged import)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        window.location.href = "/login";
      }
    });
    return () => unsubscribe();
  }, []);

  // Extract unique values for filters (unchanged behavior)
  const extractUniqueValues = (rows) => {
    return {
      operatingSystem: [...new Set(rows.map((i) => i.operatingSystem))].filter(Boolean),
      city: [...new Set(rows.map((i) => i.city))].filter(Boolean),
      region: [...new Set(rows.map((i) => i.region))].filter(Boolean),
      country: [...new Set(rows.map((i) => i.country))].filter(Boolean),
      timestamp: [
        ...new Set(
          rows
            .filter((i) => i.timestamp)
            .map((i) => {
              const d = i.timestamp?.toDate ? i.timestamp.toDate() : new Date(i.timestamp);
              return isNaN(d.getTime()) ? null : d.toISOString().split("T")[0];
            })
        ),
      ].filter(Boolean),
    };
  };

  // Fetch data (kept your query/limit/order)
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const locationLogsQuery = query(
          collection(db, "locationLogs"),
          orderBy("timestamp", "desc"),
          limit(100)
        );
        const querySnapshot = await getDocs(locationLogsQuery);
        const documents = querySnapshot.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            operatingSystem: d.operatingSystem || "N/A",
            city: d.city || "N/A",
            region: d.region || "N/A",
            country: d.country || "N/A",
            timestamp: d.timestamp?.toDate ? d.timestamp.toDate() : d.timestamp || null,
          };
        });

        if (!cancelled) {
          setData(documents);
          setFilteredData(documents);
          setUniqueValues(extractUniqueValues(documents));
        }
      } catch (e) {
        if (!cancelled) setError("Error fetching Firestore data");
        // still console.error for debugging
        // eslint-disable-next-line no-console
        console.error("Error fetching Firestore data:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Logout (kept)
  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.reload();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error logging out:", err);
    }
  };

  // Filter toggles (kept)
  const handleCheckboxChange = (category, value) => {
    setFilters((prev) => {
      const set = new Set(prev[category]);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...prev, [category]: Array.from(set) };
    });
  };

  const clearAllFilters = () => {
    setFilters({
      operatingSystem: [],
      city: [],
      region: [],
      country: [],
      timestamp: [],
    });
  };

  // Apply filters (kept logic, small tidy)
  useEffect(() => {
    const filtered = data.filter((item) => {
      const itemDate = item.timestamp
        ? (() => {
            const d = item.timestamp?.toDate ? item.timestamp.toDate() : new Date(item.timestamp);
            return isNaN(d.getTime()) ? "N/A" : d.toISOString().split("T")[0];
          })()
        : "N/A";

      return (
        (filters.operatingSystem.length === 0 ||
          filters.operatingSystem.includes(item.operatingSystem)) &&
        (filters.city.length === 0 || filters.city.includes(item.city)) &&
        (filters.region.length === 0 || filters.region.includes(item.region)) &&
        (filters.country.length === 0 || filters.country.includes(item.country)) &&
        (filters.timestamp.length === 0 || filters.timestamp.includes(itemDate))
      );
    });
    setFilteredData(filtered);
  }, [filters, data]);

  if (!isAuthenticated) return <h2 className="page-status">Authenticating...</h2>;
  if (loading) return <h2 className="page-status">Loading data...</h2>;

  // Dropdown Filter (keeps your structure/classNames; switched to click-to-toggle with escape/outside-close)
  const DropdownFilter = ({ category, values, label = "Filter" }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // close on outside click / ESC
    useEffect(() => {
      const onClick = (e) => {
        if (!ref.current) return;
        if (!ref.current.contains(e.target)) setOpen(false);
      };
      const onKey = (e) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("mousedown", onClick);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("mousedown", onClick);
        document.removeEventListener("keydown", onKey);
      };
    }, []);

    return (
      <div className="dropdown" ref={ref}>
        <button
          type="button"
          className="dropbtn"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {label}
          {filters[category].length > 0 && (
            <span className="chip">{filters[category].length}</span>
          )}
        </button>
        <div className={`dropdown-content ${open ? "open" : ""}`} role="menu">
          {values.length === 0 ? (
            <div className="muted">No options</div>
          ) : (
            values.map((value) => (
              <label key={value}>
                <input
                  type="checkbox"
                  checked={filters[category].includes(value)}
                  onChange={() => handleCheckboxChange(category, value)}
                />
                {value}
              </label>
            ))
          )}
          {values.length > 0 && (
            <div className="dropdown-actions">
              <button
                type="button"
                className="small-btn"
                onClick={() =>
                  setFilters((prev) => ({ ...prev, [category]: [] }))
                }
              >
                Clear
              </button>
              <button type="button" className="small-btn" onClick={() => setOpen(false)}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="protected-page">
      <div className="header">
        <h2>Fetched Data from Firestore (locationLogs):</h2>
        <div className="header-actions">
          <button className="ghost-button" onClick={clearAllFilters}>
            Clear Filters
          </button>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <table className="data-table">
        <thead>
          <tr>
            <th>
              Operating System
              <DropdownFilter
                category="operatingSystem"
                values={uniqueValues.operatingSystem || []}
                label="Filter"
              />
            </th>
            <th>
              City
              <DropdownFilter category="city" values={uniqueValues.city || []} label="Filter" />
            </th>
            <th>
              Region
              <DropdownFilter
                category="region"
                values={uniqueValues.region || []}
                label="Filter"
              />
            </th>
            <th>
              Country
              <DropdownFilter
                category="country"
                values={uniqueValues.country || []}
                label="Filter"
              />
            </th>
            <th>
              Timestamp (Dates)
              <DropdownFilter
                category="timestamp"
                values={(uniqueValues.timestamp || []).sort().reverse()}
                label="Filter"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredData.length === 0 ? (
            <tr>
              <td colSpan="5" className="empty-row">
                No results. Try adjusting filters.
              </td>
            </tr>
          ) : (
            filteredData.map((item) => (
              <tr key={item.id}>
                <td>{item.operatingSystem}</td>
                <td>{item.city}</td>
                <td>{item.region}</td>
                <td>{item.country}</td>
                <td>{formatDate(item.timestamp)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ProtectedPage;
